/**
 * PitcherSummaryCard — two-column pitcher overview
 * Left: grade pills + counting stats grid  |  Right: movement chart
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Chip, useTheme, alpha,
  CircularProgress,
} from '@mui/material';
import PitchMovementChart from '../charts/PitchMovementChart';
import PitchGradeCard, { calculateGrade, STAT_THRESHOLDS } from '../charts/PitchGradeCard';
import { pitchSplitsApi } from '../../services/apiService';

// Compact stat cell used in the counting grid
const StatCell = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      textAlign: 'center', py: 0.65, px: 0.5,
      borderRadius: 1.5,
      bgcolor: alpha(theme.palette.action.hover, 0.05),
      border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
    }}>
      <Typography fontWeight={800} sx={{ fontSize: '0.92rem', lineHeight: 1.15, color: 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </Typography>
    </Box>
  );
};

// Parse IP like "5.1" = 5.333
const parseIP = (ip) => {
  if (!ip) return 0;
  if (typeof ip === 'number') return ip;
  const s = String(ip);
  if (s.includes('.')) {
    const [w, f] = s.split('.');
    const frac = parseInt(f) || 0;
    return (parseInt(w) || 0) + (frac === 0 ? 0 : frac / 3);
  }
  return parseFloat(s) || 0;
};

const PitcherSummaryCard = ({ player, seasonStats, gameLog = [], year }) => {
  const theme = useTheme();
  const [pitchData, setPitchData] = useState(null);
  const [pitchLoading, setPitchLoading] = useState(false);

  const pitching = seasonStats?.pitching;
  const team = player?.team;
  const name = player?.name;

  // Load pitch-level data
  useEffect(() => {
    if (!team || !name) return;
    setPitchLoading(true);
    pitchSplitsApi.getPitcherPitchData(team, name.replace(/\s+/g, '_'), year)
      .then(data => setPitchData(data))
      .catch(() => setPitchData(null))
      .finally(() => setPitchLoading(false));
  }, [team, name, year]);

  // Calculate pitching stats
  const stats = useMemo(() => {
    if (!pitching) return null;
    const ip = parseIP(pitching.inningsPitched);
    const era  = ip > 0 ? (pitching.earnedRuns * 9) / ip : 0;
    const whip = ip > 0 ? ((pitching.hits || 0) + (pitching.baseOnBalls || 0)) / ip : 0;
    const k9   = ip > 0 ? ((pitching.strikeOuts || 0) * 9) / ip : 0;
    const bb9  = ip > 0 ? ((pitching.baseOnBalls || 0) * 9) / ip : 0;
    const h9   = ip > 0 ? ((pitching.hits || 0) * 9) / ip : 0;
    const hr9  = ip > 0 ? ((pitching.homeRuns || 0) * 9) / ip : 0;
    const kbb  = (pitching.baseOnBalls || 0) > 0
      ? (pitching.strikeOuts / pitching.baseOnBalls)
      : (pitching.strikeOuts || 0);
    const fip   = pitching.fip || null;
    const babip = pitching.babip || null;
    return { ip, era, whip, k9, bb9, h9, hr9, kbb, fip, babip };
  }, [pitching]);

  if (!pitching || !stats) return null;

  // Format IP for display
  const formatIP = (ip) => {
    const whole = Math.floor(ip);
    const frac  = ip - whole;
    if (frac < 0.1) return `${whole}.0`;
    if (frac < 0.5) return `${whole}.1`;
    if (frac < 0.9) return `${whole}.2`;
    return `${whole + 1}.0`;
  };

  const hasChart = !pitchLoading && pitchData?.pitchTypes && Object.keys(pitchData.pitchTypes).length > 0;

  // Grade circles
  const gradeItems = [
    { label: 'ERA',  value: stats.era.toFixed(2),  grade: calculateGrade(stats.era,  STAT_THRESHOLDS.pitching.era,  true) },
    { label: 'WHIP', value: stats.whip.toFixed(2), grade: calculateGrade(stats.whip, STAT_THRESHOLDS.pitching.whip, true) },
    { label: 'K/9',  value: stats.k9.toFixed(1),   grade: calculateGrade(stats.k9,   STAT_THRESHOLDS.pitching.k9)        },
    { label: 'BB/9', value: stats.bb9.toFixed(1),  grade: calculateGrade(stats.bb9,  STAT_THRESHOLDS.pitching.bb9,  true) },
    ...(stats.fip ? [{ label: 'FIP', value: parseFloat(stats.fip).toFixed(2), grade: calculateGrade(stats.fip, STAT_THRESHOLDS.pitching.fip, true) }] : []),
    ...(pitchData?.stuffPlus ? [{
      label: 'Stuff+',
      value: String(pitchData.stuffPlus),
      grade: pitchData.stuffPlus >= 130 ? 'A+' : pitchData.stuffPlus >= 120 ? 'A' : pitchData.stuffPlus >= 110 ? 'B+' : pitchData.stuffPlus >= 100 ? 'B' : pitchData.stuffPlus >= 90 ? 'C' : 'D',
    }] : []),
  ];

  // Counting stats grid
  const countingStats = [
    { label: 'IP',   value: formatIP(stats.ip) },
    { label: 'K',    value: pitching.strikeOuts  || 0 },
    { label: 'BB',   value: pitching.baseOnBalls || 0 },
    { label: 'K/BB', value: stats.kbb.toFixed(2) },
    { label: 'H/9',  value: stats.h9.toFixed(1) },
    { label: 'HR/9', value: stats.hr9.toFixed(1) },
    ...(stats.babip ? [{ label: 'BABIP', value: stats.babip.toFixed(3) }] : []),
  ];

  return (
    <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, overflow: 'hidden', borderRadius: 2.5 }}>
      {/* ── Gradient header ── */}
      <Box sx={{
        px: 2, py: 1.1,
        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
      }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem', color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Pitching Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
          {[
            `${pitching.gamesPlayed || 0}G`,
            ...(pitching.gamesStarted > 0 ? [`${pitching.gamesStarted}GS`] : []),
            `${pitching.wins || 0}W–${pitching.losses || 0}L`,
            ...(pitching.saves > 0  ? [`${pitching.saves}SV`]  : []),
            ...(pitching.holds > 0  ? [`${pitching.holds}HLD`] : []),
          ].map((lbl, i) => (
            <Chip key={i} label={lbl} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: alpha('#fff', 0.18), color: 'white', border: 'none', height: 20 }} />
          ))}
        </Box>
      </Box>

      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Grid container spacing={2} alignItems="center">

          {/* ── Left: grade pills + counting stats grid ── */}
          <Grid item xs={12} md={hasChart ? 7 : 12}>
            {/* Grade circles — evenly spaced, no awkward wrapping */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.75 }}>
              {gradeItems.map(({ label, value, grade }) => (
                <PitchGradeCard key={label} label={label} grade={grade} value={value} size="medium" />
              ))}
            </Box>

            {/* Counting stats — equal-column grid */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(countingStats.length, 4)}, 1fr)`,
              gap: 0.75,
            }}>
              {countingStats.map((s) => (
                <StatCell key={s.label} label={s.label} value={s.value} />
              ))}
            </Box>
          </Grid>

          {/* ── Right: pitch movement chart ── */}
          {(hasChart || pitchLoading) && (
            <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {pitchLoading ? (
                <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={22} />
                </Box>
              ) : (
                <PitchMovementChart pitchData={pitchData} size={230} throws={player?.throws || player?.pitchHand || 'R'} />
              )}
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PitcherSummaryCard;
